import type {
    IndustryNodeId,
    IndustryTaxonomyIndex,
} from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import { createFuzzySearchIndex } from "app/pages/sales-intelligence/services/fuzzy-search/factory";
import { normalizeForSearch } from "app/pages/sales-intelligence/services/fuzzy-search/normalize";
import type { FuzzyFuseOptions } from "app/pages/sales-intelligence/services/fuzzy-search/types";

import type { IIndustrySearchIndex, IndustrySearchDoc } from "./types";

const SHORT_QUERY_THRESHOLD = 0.15;
const LONG_QUERY_THRESHOLD = 0.2;
const SHORT_QUERY_LENGTH = 3;
const MIN_UNANCHORED_QUERY_LENGTH = 3;
const MAX_RESULTS = 50;

/**
 * Fuse scores a hit as `errors / queryLength + matchPosition / distance`, so `distance` trades
 * directly against the error budget. Industry labels are multi-word, which puts the meaningful term
 * 30+ characters in ("Consumer Electronics and Appliances Retail"); at fuse's default distance that
 * position alone outweighs two real typos, and the threshold has to be loosened to compensate —
 * which is what let a different word entirely score as a match. Keeping distance large makes
 * position a ranking nudge rather than a gate, so the threshold can stay tight enough to mean
 * "roughly this word".
 */
const FUSE_OPTIONS: FuzzyFuseOptions<IndustrySearchDoc> = {
    shouldSort: true,
    distance: 1000,
    threshold: LONG_QUERY_THRESHOLD,
    includeScore: true,
    includeMatches: true,
    keys: [{ name: "name", weight: 1 }],
};

const WORD_CHARACTER = /[\p{L}\p{N}]/u;

const occursAtWordBoundary = (name: string, query: string): boolean => {
    for (let at = name.indexOf(query); at !== -1; at = name.indexOf(query, at + 1)) {
        if (at === 0 || !WORD_CHARACTER.test(name[at - 1])) {
            return true;
        }
    }

    return false;
};

/**
 * One or two characters carry no useful fuzziness — every short run of letters occurs mid-word
 * somewhere ("ai" inside "Hair", "Dairy") — so at that length a hit must start a word to count.
 */
const acceptHit = (doc: IndustrySearchDoc, normalizedQuery: string): boolean =>
    normalizedQuery.length >= MIN_UNANCHORED_QUERY_LENGTH || occursAtWordBoundary(doc.name, normalizedQuery);

/**
 * Only the node's own name is indexed, never its ancestor path. Indexing the path looks appealing
 * ("software crm" would match), but then a bare parent term matches every descendant, the match set
 * becomes the whole subtree, and revealing it mounts the entire branch.
 */
const buildDocs = (index: IndustryTaxonomyIndex, locale: string): readonly IndustrySearchDoc[] =>
    [...index.byId.values()].map((node) => ({ id: node.id, name: normalizeForSearch(node.name, locale) }));

const scoreThresholdFor = (queryLength: number): number =>
    queryLength <= SHORT_QUERY_LENGTH ? SHORT_QUERY_THRESHOLD : LONG_QUERY_THRESHOLD;

/**
 * Fuzzy match over the taxonomy. The underlying engine caches its normalised documents on the
 * taxonomy reference, so the O(n) normalisation happens on the first keystroke rather than on page
 * load, and never again for that taxonomy.
 */
export const createIndustrySearchIndex = (): IIndustrySearchIndex => {
    const engine = createFuzzySearchIndex<IndustryTaxonomyIndex, IndustryNodeId, IndustrySearchDoc>({
        buildDocs,
        fuseOptions: FUSE_OPTIONS,
        scoreThresholdFor,
        acceptHit,
        limit: MAX_RESULTS,
    });

    return {
        match: ({ taxonomy, query, locale }) =>
            new Set(engine.search({ source: taxonomy, query, locale }).map((hit) => hit.id)),
        searchHits: ({ taxonomy, query, locale }) =>
            engine.search({ source: taxonomy, query, locale }).map((hit) => ({
                id: hit.id,
                nameRanges: hit.rangesByKey.get("name") ?? [],
            })),
        invalidate: engine.invalidate,
    };
};
